import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  IconButton,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Paper
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Settings as SettingsIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useTranslation } from '../../hooks/useTranslation';
import { apiService } from '../../services/api';

interface SystemConfigDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (config: SystemConfig) => void;
}

interface SystemConfig {
  daysToAdd: number;
  description?: string;
}

const SystemConfigDialog: React.FC<SystemConfigDialogProps> = ({
  open,
  onClose,
  onSave
}) => {
  const { t } = useTranslation();
  const [config, setConfig] = useState<SystemConfig>({
    daysToAdd: 7
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // 載入當前配置
  useEffect(() => {
    if (open) {
      loadCurrentConfig();
    }
  }, [open]);

  const loadCurrentConfig = async () => {
    try {
      setLoading(true);
      setError(null);

      // 從後端 API 獲取當前配置
      const response = await apiService.getSystemConfig();
      setConfig({
        daysToAdd: response.data.daysToAdd || 7,
        description: response.data.description
      });
    } catch (err: any) {
      console.error('載入系統配置失敗:', err);
      setError('載入系統配置失敗: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // 驗證輸入
      if (config.daysToAdd < 1 || config.daysToAdd > 365) {
        setError('天數必須在 1-365 之間');
        return;
      }

      // 保存配置到後端
      await apiService.updateSystemConfig(config);

      setSuccess('系統配置已成功更新');

      // 通知父組件配置已更新
      onSave(config);

      // 延遲關閉對話框以顯示成功消息
      setTimeout(() => {
        onClose();
      }, 1500);

    } catch (err: any) {
      console.error('保存系統配置失敗:', err);
      setError('保存系統配置失敗: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      setError(null);
      setSuccess(null);
      onClose();
    }
  };

  const handleDaysToAddChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value) || 0;
    setConfig(prev => ({
      ...prev,
      daysToAdd: value
    }));
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={saving}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SettingsIcon color="primary" />
          <Typography variant="h6" component="div">
            {t('pages.settings.systemConfig.title')}
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          disabled={saving}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              {t('common.loading')}...
            </Typography>
          </Box>
        ) : (
          <Box sx={{ mt: 2 }}>
            {/* 錯誤和成功消息 */}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}

            {/* 配置說明 */}
            <Paper sx={{ p: 2, mb: 3, bgcolor: 'info.light', color: 'info.contrastText' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <InfoIcon sx={{ mr: 1 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  {t('pages.settings.systemConfig.info.title')}
                </Typography>
              </Box>
              <Typography variant="body2">
                {t('pages.settings.systemConfig.info.description')}
              </Typography>
            </Paper>

            <Grid container spacing={3}>
              {/* Days To Add 配置 */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('pages.settings.systemConfig.daysToAdd.label')}
                  type="number"
                  value={config.daysToAdd}
                  onChange={handleDaysToAddChange}
                  helperText={t('pages.settings.systemConfig.daysToAdd.helperText')}
                  inputProps={{
                    min: 1,
                    max: 365,
                    step: 1
                  }}
                  disabled={saving}
                  error={config.daysToAdd < 1 || config.daysToAdd > 365}
                />
              </Grid>

              {/* 當前配置顯示 */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  {t('pages.settings.systemConfig.currentConfig.title')}
                </Typography>
                <Box sx={{
                  p: 2,
                  bgcolor: 'grey.50',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'grey.300'
                }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>{t('pages.settings.systemConfig.daysToAdd.label')}:</strong> {config.daysToAdd} {t('pages.settings.systemConfig.daysToAdd.unit')}
                  </Typography>
                  {config.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      <strong>{t('pages.settings.systemConfig.description')}:</strong> {config.description}
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={handleClose}
          disabled={saving}
          color="inherit"
        >
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving || loading || config.daysToAdd < 1 || config.daysToAdd > 365}
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
        >
          {saving ? t('common.saving') : t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SystemConfigDialog;
